import { type Dispatch, type SetStateAction, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import {
  BellIcon,
  BellOffIcon,
  ChevronDownIcon,
  ReplyAllIcon,
  SendIcon,
  SquarePenIcon,
  Trash2Icon,
  Undo2Icon,
} from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import { asMutation } from "simplydo/core/query";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { Checkbox } from "components/lib/UI";
import { DndContext } from "components/lib/dnd/DndContext";
import { Sortable } from "components/lib/dnd/Sortable";

import type { ChallengeDetail, ChallengeQuestion } from "../../types";

type QuestionListProps = {
  questions?: ChallengeQuestion[];
  challenge: ChallengeDetail;
  selectedQuestions?: string[];
  setSelectedQuestions?: Dispatch<SetStateAction<string[]>>;
  setQuestionToEdit?: (question: ChallengeQuestion) => void;
  saveAndMakePublic?: (
    question: ChallengeQuestion,
    notifyUsers: boolean,
  ) => void;
  nonAdminView: boolean;
};

const QuestionList = ({
  questions = [],
  challenge,
  setQuestionToEdit,
  selectedQuestions = [],
  setSelectedQuestions,
  saveAndMakePublic = () => {},
  nonAdminView,
}: QuestionListProps) => {
  const [activeQuestions, setActiveQuestions] = useState<string[]>([]);
  const user = useAppSelector((state) => state.user);
  const isEditable = !!setQuestionToEdit;

  const canManageQuestions =
    util.hasPermission(user, "challenge.manageQuestions", challenge._id) ||
    util.hasPermission(user, "org.manageChallenges", challenge.organisation);

  const reorderQuestionsMutation = useMutation({
    mutationKey: ["challenges", challenge._id, "questions", "order"],
    mutationFn: asMutation(api.challenges.reorderQuestion)!,
    onSuccess: () => {
      api.invalidateQueries(api.challenges.getQuestions, [challenge._id]);
    },
  });

  const unpublishMutation = useMutation({
    mutationKey: ["challenges", challenge._id, "questions", "unpublish"],
    mutationFn: asMutation(api.challenges.unpublishQuestion)!,
    onSuccess: (result) => {
      api.invalidateQueries(api.challenges.get, [challenge._id]);
      api.setQueryData(
        api.challenges.getQuestions,
        [challenge._id],
        (prev) => ({
          ...prev,
          questions: prev.questions
            .filter((q) => q._id !== result.question._id)
            .concat([result.question]),
        }),
      );
      api.invalidateQueries(api.challenges.getQuestions, [challenge._id]);
    },
  });

  const deleteQuestionMutation = useMutation({
    mutationKey: ["challenges", challenge._id, "questions"],
    mutationFn: asMutation(api.challenges.deleteQuestion)!,
    onSuccess: (result) => {
      api.setQueryData(
        api.challenges.getQuestions,
        [challenge._id],
        (prev) => ({
          ...prev,
          questions: prev.questions.filter((q) => q._id !== result.deleted),
        }),
      );
      api.invalidateQueries(api.challenges.getQuestions, [challenge._id]);
    },
  });

  return (
    <Accordion fluid styled exclusive={false}>
      <DndContext
        onDragEnd={(event) => {
          const { source, target } = event.operation;
          if (!source || !target) {
            return;
          }

          const sourceIndex =
            (source as { initialIndex?: number; index?: number })
              .initialIndex ??
            (source as { index?: number }).index ??
            0;
          const destinationIndex = (target as { index?: number }).index ?? 0;
          if (sourceIndex === destinationIndex) return;

          const sourceQuestion = questions.find(
            (question) => question._id === source.id,
          );
          if (sourceQuestion) {
            api.setQueryData(
              api.challenges.getQuestions,
              [challenge._id],
              (prev) => ({
                ...prev,
                questions: prev.questions
                  .filter((q) => q._id !== source.id)
                  .flatMap((q) => {
                    if (sourceQuestion.published && !q.published) {
                      return [q];
                    }
                    if (!sourceQuestion.published && q.published) {
                      return [q];
                    }
                    if (q.order === destinationIndex) {
                      if (sourceIndex < destinationIndex) {
                        return [q, sourceQuestion];
                      }
                      return [sourceQuestion, q];
                    }
                    return [q];
                  }),
              }),
            );

            reorderQuestionsMutation.mutate([
              challenge._id,
              source.id as string,
              destinationIndex,
            ]);
          }
        }}
      >
        {questions.map((question, index) => (
          <Sortable
            index={index}
            key={question._id}
            id={question._id}
            disabled={!isEditable || !canManageQuestions}
          >
            <AccordionTitle
              active={activeQuestions.includes(question._id)}
              index={question.order}
              style={{ color: "rgba(0, 0, 0, .87)" }}
              onClick={() => {
                setActiveQuestions((prev) => {
                  if (prev.includes(question._id)) {
                    return prev.filter((i) => i !== question._id);
                  }
                  return [...prev, question._id];
                });
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ maxWidth: "85%", alignSelf: "center" }}>
                  {isEditable && canManageQuestions ? (
                    <div
                      style={{
                        marginBottom: 10,
                        fontWeight: "normal",
                        color: "grey",
                      }}
                    >
                      <UserChip user={question.ownerCreatedBy} compact="very" />
                      {", "}
                      {formatDateAt(question.createdAt)}:
                    </div>
                  ) : null}
                  <div>
                    <span style={{ fontSize: "1em" }}>{question.question}</span>
                    <span
                      style={{ marginLeft: 12, position: "relative", top: -2 }}
                    >
                      {question.announcedAt && canManageQuestions && (
                        <Label size="small" color="blue">
                          Last announced: {formatDateAt(question.announcedAt)}
                        </Label>
                      )}
                    </span>
                    <span style={{ marginLeft: 12 }} />
                    <span
                      style={{
                        position: "relative",
                        top: -2,
                        display: "inline-flex",
                        gap: 4,
                      }}
                    >
                      {!question.answer ? (
                        <Label size="small">Awaiting answer</Label>
                      ) : null}
                      {canManageQuestions ? (
                        !question.published ? (
                          <Label size="small" color="yellow">
                            Not public
                          </Label>
                        ) : null
                      ) : null}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: isEditable ? 5 : 0,
                    gap: 3,
                  }}
                >
                  {isEditable && canManageQuestions ? (
                    <Dropdown
                      direction="left"
                      trigger={
                        <Button variant="outline">
                          <Icon icon={ChevronDownIcon} />
                        </Button>
                      }
                    >
                      <DropdownMenu>
                        {question.published ? (
                          <DropdownItem
                            onClick={(e) => {
                              e.stopPropagation();
                              util
                                .confirm(
                                  "Make question non-public",
                                  "Are you sure you want to unpublish this question? Users will no longer be able to see the question or its response.",
                                )
                                .then(() => {
                                  unpublishMutation.mutate([
                                    challenge._id,
                                    question._id,
                                  ]);
                                })
                                .catch(() => {});
                            }}
                          >
                            <Icon icon={Undo2Icon} />
                            Make non-public
                          </DropdownItem>
                        ) : question.answer ? (
                          <Dropdown
                            trigger={
                              <DropdownItem>
                                <Icon icon={SendIcon} />
                                Make public
                              </DropdownItem>
                            }
                          >
                            <DropdownMenu>
                              <DropdownItem
                                onClick={() =>
                                  saveAndMakePublic(question, true)
                                }
                              >
                                <Icon icon={BellIcon} />
                                Make public and notify users
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  saveAndMakePublic(question, false)
                                }
                              >
                                <Icon icon={BellOffIcon} />
                                Make public without notifying users
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        ) : (
                          <DropdownItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuestionToEdit(question);
                            }}
                          >
                            <Icon icon={ReplyAllIcon} />
                            Answer now
                          </DropdownItem>
                        )}
                        <DropdownItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setQuestionToEdit(question);
                          }}
                        >
                          <Icon icon={SquarePenIcon} />
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          onClick={(e) => {
                            e.stopPropagation();
                            util
                              .confirm(
                                "Delete question",
                                "Are you sure you want to delete this question?",
                              )
                              .then(() => {
                                deleteQuestionMutation.mutate([
                                  challenge._id,
                                  question._id,
                                ]);
                              })
                              .catch(() => {});
                          }}
                        >
                          <Icon icon={Trash2Icon} />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  ) : null}
                  {!nonAdminView &&
                    canManageQuestions &&
                    setSelectedQuestions &&
                    question.answer && (
                      <Checkbox
                        isSelected={selectedQuestions.includes(question._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (!selectedQuestions.includes(question._id)) {
                            setSelectedQuestions((prev) => [
                              ...prev,
                              question._id,
                            ]);
                          } else {
                            setSelectedQuestions((prev) =>
                              prev.filter((q) => q !== question._id),
                            );
                          }
                        }}
                      />
                    )}
                </div>
              </div>
            </AccordionTitle>
            <AccordionContent
              active={activeQuestions.includes(question._id)}
              style={{ paddingTop: 0, marginLeft: 20, paddingBottom: 5 }}
            >
              {question.answer ? (
                <RichTextViewer value={question.answer || ""} />
              ) : (
                <>
                  <p style={{ color: "#ababab" }}>
                    Question has not been answered yet.
                  </p>
                  {isEditable && canManageQuestions ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setQuestionToEdit(question);
                      }}
                      color="primary"
                    >
                      Answer now
                    </Button>
                  ) : null}
                </>
              )}
            </AccordionContent>
          </Sortable>
        ))}
      </DndContext>
    </Accordion>
  );
};

export default QuestionList;
